import { takeUntil } from 'rxjs/operators';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject, Injector,
  OnInit,
  signal,
  TemplateRef,
  ViewChild, OnDestroy,
} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { PaginatorModule } from 'primeng/paginator';
import { ToastModule } from 'primeng/toast';
import { FileUploadModule } from 'primeng/fileupload';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';

import { DictionaryItem } from '@services/dictionaries-api.service';
import { BaseService } from '@6grain/masofa-monolith-api-client/common/api.base.service';
import { SystemDocumentationService } from '@6grain/masofa-monolith-api-client/common/api/systemDocumentation.service';
import { SystemDocumentationWithFileSize } from '@6grain/masofa-monolith-api-client/common';
import { ServerTranslateService } from '@services/server-translate.service';
import { TableConfig } from '@components/data-table/data-table.component';
import { BackButton } from '@components/ui/back-button/back-button';
import { Filters } from '@components/views/filters/filters';
import { HttpClient } from '@angular/common/http';
import { environment } from '@root/src/environments/environment';
import { AddSystemDocumentationForm } from './forms/add-system-documentation-form/add-system-documentation-form';
import {
  ListTableWithoutFiltersWrapper
} from '@app/wrappers/pages/list-table-without-filters-wrapper/list-table-without-filters-wrapper';
import { buildFilterQuery } from '@app/utils/_msf';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { FilterOperator as FilterOperatorLocal } from '@app/utils/filter-operator';
import {FileStorageItemService} from '@6grain/masofa-monolith-api-client/common/api/fileStorageItem.service';

interface TableItem {
  header: string;
  field: string;
  metaField?: string;
  isBoolean?: boolean;
  isDate?: boolean;
  parseFunction?: (field: any) => string;
  template?: string;
  type?: string;
  customTemplate?: TemplateRef<unknown>;
}

@Component({
  selector: 'app-system-documentation-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    CheckboxModule,
    ProgressSpinnerModule,
    CardModule,
    DialogModule,
    TooltipModule,
    PaginatorModule,
    TranslateModule,
    BackButton,
    Filters,
    ToastModule,
    FileUploadModule,
    ListTableWithoutFiltersWrapper,
  ],
  providers: [MessageService, DialogService, DateFormatLangPipe, DatePipe],
  templateUrl: './system-documentation-page.html',
  styleUrls: ['./system-documentation-page.scss']
})
export class SystemDocumentationPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private readonly router: Router;
  private readonly destroyRef: DestroyRef;
  public readonly translateService: TranslateService;
  private readonly serverTranslateService: ServerTranslateService;
  private readonly http: HttpClient;
  private readonly messageService: MessageService;
  private readonly dialogService: DialogService;
  private readonly fileStorageItemService: FileStorageItemService;
  readonly fileDownloadBase = `${environment.api}/common/FileStorageItem/GetFile/`;
  private readonly systemDocumentationService: SystemDocumentationService;

  // View templates
  @ViewChild('actionsTemplate') actionsTemplate!: TemplateRef<unknown>;
  @ViewChild('booleanTemplate') booleanTemplate!: TemplateRef<unknown>;
  @ViewChild('nameTemplate') nameTemplate!: TemplateRef<unknown>;
  @ViewChild('fileDownloadTemplate') fileDownloadTemplate!: TemplateRef<unknown>;
  @ViewChild('blockTemplate') blockTemplate!: TemplateRef<unknown>;

  readonly loading = signal<boolean>(true);
  readonly items = signal<DictionaryItem[]>([]);
  readonly totalRecords = signal<number>(0);
  readonly showModal = signal<boolean>(false);
  readonly isSaving = signal<boolean>(false);
  readonly showDeleteModal = signal<boolean>(false);
  readonly isDeleting = signal<boolean>(false);
  readonly lastUpdateTime = signal<Date | null>(null);
  readonly systemDocumentationBlocks = signal<any[]>([]);
  readonly metaData = signal<any>({});

  // State
  template: any = {
    title: 'System Documentation',
    service: SystemDocumentationService,
    queryPostMethod: 'commonSystemDocumentationCustomGetByQueryPost',
    table: [
      {
        header: 'Creation Date',
        field: 'createAt',
        isDate: true
      },
      {
        header: 'Name',
        field: 'names'
      },
      {
        header: 'Block',
        field: 'blockId',
        metaField: 'systemDocumentationBlocks'
      },
      {
        header: 'Comment',
        field: 'comment'
      },
      {
        header: 'Size',
        field: 'fileSizeString'
      },
      {
        header: 'File',
        field: 'fileDownload',
        type: 'custom'
      },
      {
        header: 'Publication',
        field: 'visible',
        isBoolean: true
      }
    ],
    filters: [
      {
        name: 'names',
        title: 'Name',
        type: 'input'
      },
      {
        name: 'blockId',
        title: 'Block',
        type: 'select',
        metaField: 'systemDocumentationBlocks'
      },
      {
        name: 'visible',
        title: 'Published',
        type: 'checkbox'
      }
    ],
    meta: {
      systemDocumentationBlocks: {
        field: [],
        displayField: 'names',
        service: null // Will be set after API client generation
      }
    }
  };
  filters: any = {};
  parentRoute = '/admin';
  first = 0;
  take = 10;
  modalData: any = {};
  editingItem: DictionaryItem | null = null;
  itemToDelete: DictionaryItem | null = null;
  filtersParams: Object[] = [];
  toggleFilter = false;
  apiService!: BaseService;
  ref: DynamicDialogRef | undefined;

  tableConfig: TableConfig = {
    selectable: false,
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [10, 25, 50, 100],
    loading: false,
    emptyMessage: 'No data',
  };

  constructor(
    public injector:Injector
  ) {
    this.router = inject(Router);
    this.destroyRef = inject(DestroyRef);
    this.translateService = inject(TranslateService);
    this.serverTranslateService = inject(ServerTranslateService);
    this.http = inject(HttpClient);
    this.messageService = inject(MessageService);
    this.dialogService = inject(DialogService);
    this.fileStorageItemService = inject(FileStorageItemService);
    this.systemDocumentationService = inject(SystemDocumentationService);
  }

  ngOnInit(): void {
    this.initializeComponent();
    this.setupLanguageChangeListener();
    this.loadSystemDocumentationBlocks();
  }

  ngAfterViewInit(): void {
    this.setupTableTemplates();
  }

  private initializeComponent(): void {
    this.apiService = this.systemDocumentationService;
    this.setupMultilingualColumns();
    this.tableConfig.emptyMessage = this.translateService.instant('No data');
  }

  private loadSystemDocumentationBlocks(): void {
    const apiUrl = `${environment.api}/dictionaries/SystemDocumentationBlock/GetByQuery`;

    this.http.post<any[]>(apiUrl, {
      offset: 0,
      take: 9999,
      filters: []
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const blocks = response || [];
          this.systemDocumentationBlocks.set(blocks);
          // Обновляем metaData для передачи в Filters компонент
          this.metaData.set({
            systemDocumentationBlocks: blocks
          });
        },
        error: (error) => {
          console.error('Error loading system documentation blocks:', error);
          this.systemDocumentationBlocks.set([]);
          this.metaData.set({
            systemDocumentationBlocks: []
          });
        }
      });
  }

  private setupLanguageChangeListener(): void {
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.tableConfig.emptyMessage = this.translateService.instant('No data');
      });
  }

  // -----------------------------
  // Filter Operations
  // -----------------------------
  applyFilters(): void {
    this.filtersParams = buildFilterQuery(this.filters);

    this.filtersParams.forEach((param: any) => {
      if ((param.filterField === 'names' || param.filterField === 'fullName') && param.filterValue) {
        param.filterOperator = FilterOperatorLocal.Contains;
      }
    });

    this.toggleFilter = !this.toggleFilter;
  }

  clearFilters(): void {
    this.filters = {};
    this.filtersParams = buildFilterQuery(this.filters);
    this.toggleFilter = !this.toggleFilter;
  }

  // -----------------------------
  // Modal Operations
  // -----------------------------
  openAddModal(): void {
    this.openSystemDocumentationModal('create');
  }

  openEditModal(item: DictionaryItem): void {
    this.openSystemDocumentationModal('edit', item);
  }

  private openSystemDocumentationModal(mode: 'create' | 'edit', model?: DictionaryItem): void {
    this.ref = this.dialogService.open(AddSystemDocumentationForm, {
      draggable: false,
      resizable: false,
      header: this.translateService.instant(mode === 'create' ? 'Add system documentation' : 'Edit system documentation'),
      width: '60vw',
      height: '100vh',
      closable: true,
      modal: true,
      data: { mode, model }
    });

          this.ref.onClose
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
              if (result?.success) {
                this.loadSystemDocumentationBlocks(); // Обновляем блоки после сохранения
                this.loadData();
              }
            });
  }

  // -----------------------------
  // Delete Operations
  // -----------------------------
  deleteItem(item: DictionaryItem): void {
    this.itemToDelete = item;
    this.showDeleteModal.set(true);
  }

  confirmDelete(): void {
    if (!this.itemToDelete) return;

    this.isDeleting.set(true);
    this.systemDocumentationService
      .commonSystemDocumentationDeleteIdDelete(this.itemToDelete.id!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isDeleting.set(false);
          this.showDeleteModal.set(false);
          this.itemToDelete = null;
          this.loadData();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success'),
            detail: this.translateService.instant('Item deleted successfully')
          });
        },
        error: (error) => {
          this.isDeleting.set(false);
          console.error('Error deleting item:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('Error'),
            detail: this.translateService.instant('Failed to delete item')
          });
        }
      });
  }

  cancelDelete(): void {
    this.showDeleteModal.set(false);
    this.itemToDelete = null;
  }

  // -----------------------------
  // Export
  // -----------------------------
  exportData(): void {
    if (!this.items()?.length) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('Warning'),
        detail: this.translateService.instant('No data to export')
      });
      return;
    }

    const exportParams: any = {
      offset: 0,
      take: 999999,
      filters: buildFilterQuery(this.filters)
    };

    this.loading.set(true);
    this.systemDocumentationService
      .commonSystemDocumentationCustomGetByQueryPost(exportParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const allItems = response || [];
          const csvData = this.convertToCSV(allItems);
          this.downloadCSV(csvData, `system-documentation_${new Date().toISOString().split('T')[0]}.csv`);
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success'),
            detail: this.translateService.instant('Data exported successfully')
          });
        },
        error: (error) => {
          console.error('Error exporting data:', error);
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('Error'),
            detail: this.translateService.instant('Failed to export data')
          });
        }
      });
  }

  private downloadCSV(csvData: string, filename: string): void {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private convertToCSV(items: any[]): string {
    if (!items?.length) return '';

    const headers: string[] = [];
    const fields: string[] = [];

    this.template.table
      .filter((col: TableItem) => col.field && col.header !== 'Actions')
      .forEach((col: TableItem) => {
        if (col.field === 'names') {
          environment.langs.forEach(langObj => {
            const lang =  langObj.value;
            headers.push(`Name (${this.getLanguageLabel(lang)})`);
            fields.push(`name_${lang}`);
          });
        } else {
          headers.push(col.header);
          fields.push(col.field as string);
        }
      });

    let csv = headers.join(',') + '\n';

    items.forEach(item => {
      const row = fields.map((field: string) => {
        let value: string | number | boolean | null | undefined;

        if (field.startsWith('name_')) {
          const langCode = field.replace('name_', '');
          value = item['names']?.[langCode] || '';
        } else {
          value = item[field];
        }

        if (value === null || value === undefined) {
          return '""';
        } else if (typeof value === 'boolean') {
          return `"${value ? 'Yes' : 'No'}"`;
        } else {
          return `"${String(value).replace(/"/g, '""')}"`;
        }
      });

      csv += row.join(',') + '\n';
    });

    return '\uFEFF' + csv;
  }

  private setupMultilingualColumns(): void {
    // Не разделяем на отдельные колонки, оставляем одну колонку для всех языков
    // Обновляем шаблон, чтобы показывать все языки в одной ячейке
    this.template.table.forEach((column: any) => {
      if (column.field === 'name' || column.field === 'names') {
        // Оставляем поле как есть, но используем кастомный шаблон
        if (!column.type) {
          column.type = 'custom';
        }
      } else {
        if (!column.type) {
          column.type = column.isBoolean ? 'boolean' : column.isDate ? 'date' : 'text';
        }
      }
    });
  }

  private getLanguageLabel(lang: string): string {
    const labels: Record<string, string> = {
      'ru-RU': 'RU',
      'en-US': 'EN',
      'uz-Cyrl-UZ': 'UZ',
      'uz-Latn-UZ': 'UZ'
    };
    return labels[lang] || lang;
  }

  private setupTableTemplates(): void {
    const actions: TableItem = {
      field: '',
      header: 'Actions',
      type: 'custom',
      customTemplate: this.actionsTemplate,
    };

    if (this.template.table && !this.template.table.some((item: any) => item.header === 'Actions')) {
      this.template.table.push(actions);
    }

    this.template.table?.forEach((item: any) => {
      if (item.isBoolean && (item.field === 'visible' || ['Публикация', 'Publication', 'Visible'].includes(item.header))) {
        item.type = 'custom';
        item.customTemplate = this.booleanTemplate;
      } else if (item.isBoolean) {
        item.type = 'boolean';
      } else if ((item.field === 'name' || item.field === 'names') && this.nameTemplate) {
        item.type = 'custom';
        item.customTemplate = this.nameTemplate;
      } else if (item.isMultilingual && this.nameTemplate) {
        // Для мультиязычных колонок показываем конкретное значение для языка
        item.type = 'custom';
        item.customTemplate = this.nameTemplate;
      } else if (!item.type) {
        item.type = 'text';
      }

      // Handle file download column for system documentation
      if (item.field === 'fileDownload' && item.type === 'custom' && this.fileDownloadTemplate) {
        item.customTemplate = this.fileDownloadTemplate;
      }

      // Handle blockId column with metaField
      if (item.field === 'blockId' && item.metaField === 'systemDocumentationBlocks' && this.blockTemplate) {
        item.type = 'custom';
        item.customTemplate = this.blockTemplate;
      }
    });
  }

  getBlockName(blockId: string | null | undefined): string {
    if (!blockId) return '-';

    const block = this.systemDocumentationBlocks().find(b => b.id === blockId);
    if (!block) return blockId;

    return this.getNameForLanguage(block);
  }

  getNameForLanguage(item: any): string {
    if (!item?.names) return '';

    const langMap: Record<string, string> = {
      'en': 'en-US',
      'en-us': 'en-US',
      'ru': 'ru-RU',
      'ru-ru': 'ru-RU',
      'uz': 'uz-Latn-UZ',
      'uz-latn': 'uz-Latn-UZ'
    };

    const currentLang = this.translateService.currentLang;
    const langCode = langMap[currentLang?.toLowerCase()] || '';

    return item.names[langCode] || item.names['en-US'] || item.names['ru-RU'] || item.names['uz-Latn-UZ'] || '';
  }

  getMultilingualValue(item: any, fieldName: string): string {
    if (!item?.names) return '';
    const language = fieldName.replace('name_', '');
    return item.names[language] || '';
  }

  getDictApiMethodName(apiMethodName: string): string {
    if (apiMethodName === 'GetByQueryPost') {
      return 'commonSystemDocumentationCustomGetByQueryPost';
    }
    if (apiMethodName === 'total') {
      return 'commonSystemDocumentationGetTotalCountPost';
    }
    return `commonSystemDocumentation${apiMethodName}`;
  }

  private loadData(): void {
    // Toggle the filter to trigger table reload
    this.toggleFilter = !this.toggleFilter;
    // Update last update time
    this.lastUpdateTime.set(new Date());
  }

  downloadFile(item: DictionaryItem): void {
    if (!item['fileStorageId']) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('Warning'),
        detail: this.translateService.instant('File not found')
      });
      return;
    }

    // First get file info to extract file extension
    this.fileStorageItemService.commonFileStorageItemGetByIdIdGet(item['fileStorageId']).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fileInfo) => {
          // Extract file extension from fileStoragePath
          let fileExtension = '';
          if (fileInfo.fileStoragePath) {
            const pathParts = fileInfo.fileStoragePath.split('/');
            const originalFileName = pathParts[pathParts.length - 1];
            const extensionParts = originalFileName.split('.');
            if (extensionParts.length > 1) {
              fileExtension = '.' + extensionParts[extensionParts.length - 1];
            }
          }

          // Get document name from multilingual field based on current language
          let documentName = 'document';
          if (item['names']) {
            const names = typeof item['names'] === 'string' ? JSON.parse(item['names']) : item['names'];
            const currentLang = this.translateService.currentLang;

            // Try to get name in current language, fallback to other languages
            if (names[currentLang]) {
              documentName = names[currentLang];
            } else if (names['ru-RU']) {
              documentName = names['ru-RU'];
            } else if (names['en-US']) {
              documentName = names['en-US'];
            } else if (names['uz-Latn-UZ']) {
              documentName = names['uz-Latn-UZ'];
            }
          }

          // Combine document name with file extension
          const fileName = documentName + fileExtension;

          // Download the file with correct name
          const apiUrl = `${environment.api}/common/FileStorageItem/GetFile/${item['fileStorageId']}`;

          this.http.get(apiUrl, { responseType: 'blob' }).pipe(takeUntil(this.destroy$)).subscribe({
            next: (blob: Blob) => {
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);

              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant('Success'),
                detail: this.translateService.instant('File downloaded successfully')
              });
            },
            error: (error) => {
              console.error('Error downloading file:', error);
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('Error'),
                detail: this.translateService.instant('Failed to download file')
              });
            }
          });
        },
        error: (error) => {
          console.error('Error getting file info:', error);
          // Fallback: use document name without extension
          const fileName = this.getFileName(item);
          const apiUrl = `${environment.api}/common/FileStorageItem/GetFile/${item['fileStorageId']}`;

          this.http.get(apiUrl, { responseType: 'blob' }).pipe(takeUntil(this.destroy$)).subscribe({
            next: (blob: Blob) => {
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);

              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant('Success'),
                detail: this.translateService.instant('File downloaded successfully')
              });
            },
            error: (downloadError) => {
              console.error('Error downloading file:', downloadError);
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('Error'),
                detail: this.translateService.instant('Failed to download file')
              });
            }
          });
        }
      });
  }

  private getFileName(item: DictionaryItem): string {
    if (item['names']) {
      const names = typeof item['names'] === 'string' ? JSON.parse(item['names']) : item['names'];
      const currentLang = this.translateService.currentLang;

      // Try to get name in current language, fallback to other languages
      if (names[currentLang]) {
        return names[currentLang];
      } else if (names['ru-RU']) {
        return names['ru-RU'];
      } else if (names['en-US']) {
        return names['en-US'];
      } else if (names['uz-Latn-UZ']) {
        return names['uz-Latn-UZ'];
      }
    }
    return 'document.pdf';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

